import { Component, Mixins, Ref } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data";
import {
  GatewayCardData,
  GatewayData,
  GatewayExternalCardData,
  GatewayMobileData,
  ManualPaymentData,
  PaymentMethodType,
  SelectedPaymentMethod,
  StoredCardData
} from "@/models/selectPaymentMethod";
import SelectPaymentMethod from "@/components/app/global/payments/selectPaymentMethod/index.vue";
import ExternalStoreMixin from "@/components/app/admin/oauth/externalStoreMixin";
import { IClient } from "@/models/clients";
import { QUERY_PARAMS } from "@/data/constants";

@Component
export default class PaymentProvider extends Mixins(
  RequestPromises,
  Context,
  ExternalStoreMixin
) {
  public paymentMethod: SelectedPaymentMethod | null = null;
  public paymentData: any = {};
  public hasExternalFlow = false;

  @Ref()
  public selectPaymentMethodRef!: SelectPaymentMethod;

  public get paymentMethodType(): PaymentMethodType | undefined {
    return this.paymentMethod?.type;
  }

  public get isStoredCard() {
    return this.paymentMethodType === PaymentMethodType.STORED_CARD;
  }

  public get isGatewayCard() {
    return this.paymentMethodType === PaymentMethodType.GATEWAY_CARD;
  }

  public get isGatewayBankTransfer() {
    return this.paymentMethodType === PaymentMethodType.GATEWAY_BANK_TRANSFER;
  }

  public get isGatewayDirectDebit() {
    return this.paymentMethodType === PaymentMethodType.GATEWAY_DIRECT_DEBIT;
  }

  public get isGatewaySepa() {
    return this.paymentMethodType === PaymentMethodType.GATEWAY_SEPA;
  }

  public get isGatewayMobile() {
    return this.paymentMethodType === PaymentMethodType.GATEWAY_MOBILE;
  }

  public get isGatewayOffline() {
    return this.paymentMethodType === PaymentMethodType.GATEWAY_OFFLINE;
  }

  public get isPayLater() {
    return this.paymentMethodType === PaymentMethodType.PAY_LATER;
  }

  public get isManualPayment() {
    return this.paymentMethodType === PaymentMethodType.MANUAL_PAYMENT;
  }

  public get isExternalStore() {
    return this.paymentMethodType === PaymentMethodType.EXTERNAL_STORE;
  }

  public get isExternal() {
    return !!this.$_.get(this.paymentMethod, "data.external");
  }

  public get shouldBeStored() {
    return !!this.$_.get(this.paymentMethod, "data.store");
  }

  public prepareStoreCardData() {
    const data = this.paymentMethod?.data as StoredCardData;
    this.paymentData = { payment_details_id: data.payment_details_id };
  }

  public async prepareCardGatewayData(clientId: IClient["id"]) {
    if (this.shouldBeStored) {
      const newPaymentMethod = await this.storePaymentMethod(clientId);
      this.paymentData = { payment_details_id: newPaymentMethod.id };
      return;
    }

    if (this.isExternal) {
      const data = this.paymentMethod?.data as GatewayExternalCardData;
      this.paymentData = { gateway_id: data.gateway_id };
      return;
    }

    const data = this.paymentMethod?.data as GatewayCardData;

    this.paymentData = {
      card_type: data.card_type,
      card_num: data.card_num,
      card_expire_date: data.card_expire_date,
      card_cvv: data.card_cvv,
      name: data.name,
      address_id: data.address_id,
      gateway_id: data.gateway_id,
      cardholder_name: data.cardholder_name
    };
  }

  public prepareGatewayData() {
    const data = this.paymentMethod?.data as GatewayData;
    this.paymentData = { gateway_id: data.gateway_id };
  }

  public prepareGatewayMobileData() {
    const data = this.paymentMethod?.data as GatewayMobileData;
    this.paymentData = {
      gateway_id: data.gateway_id,
      payer: data.payer
    };
  }

  public prepareManualPaymentData() {
    const data = this.paymentMethod?.data as ManualPaymentData;
    this.paymentData.gateway_id = data.gateway_id;
    this.paymentData.amount = data.amount;
    if (data.transaction_id) {
      this.paymentData.transaction_id = data.transaction_id;
    }
  }

  public async preparePaymentData(clientId: IClient["id"]) {
    this.paymentData = {};
    switch (this.paymentMethodType) {
      case PaymentMethodType.STORED_CARD:
        this.prepareStoreCardData();
        break;
      case PaymentMethodType.GATEWAY_CARD:
        await this.prepareCardGatewayData(clientId);
        break;
      case PaymentMethodType.GATEWAY_SEPA:
      case PaymentMethodType.GATEWAY_OFFLINE:
      case PaymentMethodType.GATEWAY_DIRECT_DEBIT:
      case PaymentMethodType.GATEWAY_BANK_TRANSFER:
        this.prepareGatewayData();
        break;
      case PaymentMethodType.GATEWAY_MOBILE:
        this.prepareGatewayMobileData();
        break;
      case PaymentMethodType.MANUAL_PAYMENT:
        this.prepareManualPaymentData();
        break;
    }

    if (this.paymentMethod?.amount) {
      this.paymentData.amount = this.paymentMethod?.amount;
    }

    if (!this.paymentData.amount && this.totalAmountToPay) {
      this.paymentData.amount = this.totalAmountToPay;
    }

    if (this.paymentMethod?.walletAmount) {
      this.paymentData.wallet_amount = this.paymentMethod?.walletAmount;
      if (!this.paymentMethodType) {
        this.paymentData.amount = this.paymentData.wallet_amount;
      }
    }
  }

  public storePaymentMethod(clientId: IClient["id"]) {
    const data = this.paymentMethod?.data as GatewayCardData;
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/create`,
      {
        id: clientId,
        data: {
          card_type: data.card_type,
          card_num: data.card_num,
          card_expire_date: data.card_expire_date,
          card_cvv: data.card_cvv,
          name: data.name,
          address_id: data.address_id,
          gateway_id: data.gateway_id,
          cardholder_name: data.cardholder_name,
          auto_payment: this.paymentMethod?.auto_payment
        }
      }
    );
  }

  public get hasValidPaymentMethod() {
    return !!this.paymentMethodType || !!this.paymentMethod?.walletAmount;
  }

  public get totalAmountToPay() {
    return 0;
  }

  public get additionalManualPaymentData() {
    return {};
  }

  public get additionalPaymentData() {
    return {};
  }

  public executeManualPayment() {
    return this.$store.dispatch(`data/${DataModules.PAYMENTS}/manualPayment`, {
      data: {
        ...this.paymentData,
        ...this.additionalManualPaymentData
      }
    });
  }

  public responseHasExternalFlow(paymentResponseData) {
    return !this.$_.isEmpty(
      this.$_.get(paymentResponseData, "approval_url", "")
    );
  }

  public get externalFlowRef() {
    return this.$_.get(this.selectPaymentMethodRef, "externalFlowRef");
  }

  public async executePayment(attemptKey = "") {
    // Add payment `attempt` key to query param
    if (attemptKey) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          [QUERY_PARAMS.ATTEMPT]: attemptKey
        }
      });
    }

    if (this.isManualPayment) {
      await this.executeManualPayment();
    } else if (
      this.totalAmountToPay !== 0 &&
      (![
        PaymentMethodType.GATEWAY_OFFLINE,
        PaymentMethodType.GATEWAY_BANK_TRANSFER,
        PaymentMethodType.PAY_LATER
      ].includes(this.paymentMethodType as PaymentMethodType) ||
        this.paymentData.wallet_amount > 0)
    ) {
      const paymentResponseData = await this.$store.dispatch(
        `data/${DataModules.PAYMENTS}/create`,
        {
          data: {
            ...this.paymentData,
            ...this.additionalPaymentData
          }
        }
      );

      this.hasExternalFlow = this.responseHasExternalFlow(paymentResponseData);
      if (this.hasExternalFlow) {
        if (!this.externalFlowRef) {
          throw new Error(`ExternalFlowRef not provided`);
        }
        this.externalFlowRef.handleExternalFlow(paymentResponseData);
      } else {
        this.$store.dispatch("toast/show", {
          message: this.$t("_.payment_successful")
        });
      }
    }

    return Promise.resolve();
  }
}
